import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";
import { useCreateKPA, useGetAllKPAs, useGetKPISetupByYearAndKPA, useUpdateKPA } from "service/contract-assessment.service";
import {
  useCreateKPISetup,
  useGetKPISetupByKpaAndYear,
  useUpdateKPISetup
} from "service/contract-assessment.service";
import {
  useCreateContractPerformanceAssessment,
  useGetContractPerformanceAssessmentByKpiIdAndLocation,
  useUpdateContractPerformanceAssessment
} from "service/contract-assessment.service";

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const KPA_TYPE_OPTIONS = [
  { value: "REGIONAL", label: "Regional" },
  { value: "DISTRICT", label: "District" },
  { value: "BOTH", label: "Both" },
];

const DEFAULT_KPA_FORM = {
  kpaName: "",
  kpaDescription: "",
  weight: 0.0,
  kpaType: "BOTH",
  status: "ACTIVE",
};

const DEFAULT_KPI_SETUP_FORM = {
  kpiName: "",
  scoringCriteria: "",
  minScore: 0,
  maxScore: 0,
  weight: 0.0,
  expectedStartDate: "",
  expectedEndDate: "",
  status: "ACTIVE",
};

const DEFAULT_ASSESSMENT_FORM = {
  observation: "",
  actualScore: 0,
  status: "ACTIVE",
};

function normalizeStatus(value) {
  return String(value || "").trim().toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";
}

function isActiveStatus(value) {
  return normalizeStatus(value) === "ACTIVE";
}

function formatStatusLabel(value) {
  return normalizeStatus(value) === "ACTIVE" ? "Active" : "Inactive";
}

function getKpaDisplayCode(kpa, index) {
  return kpa.code || `KPA ${index + 1}`;
}

function RowActionMenu({ isOpen, onToggle, items }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || items.length * 36 + 10;
    const menuWidth = menuRef.current?.offsetWidth || 144;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpwards = spaceBelow < menuHeight + 8;

    const top = openUpwards
      ? Math.max(8, triggerRect.top - menuHeight - 6)
      : Math.min(window.innerHeight - menuHeight - 8, triggerRect.bottom + 6);
    const left = Math.min(
      Math.max(8, triggerRect.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    );

    setMenuPosition({ top, left });
  }, [items.length]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const frame = requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div className="inline-flex" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Open actions menu"
      >
        <Icon name="MoreHorizontal" className="h-4 w-4" />
      </button>

      {isOpen
        ? createPortal(
          <div
            ref={menuRef}
            style={{ top: menuPosition.top, left: menuPosition.left }}
            className="fixed z-[1500] min-w-[9rem] rounded-md border border-border bg-card p-1 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  item.onClick();
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors ${item.tone === "destructive"
                  ? "text-destructive hover:bg-destructive/10"
                  : "text-foreground hover:bg-muted"
                  }`}
              >
                <Icon name={item.icon} className="h-3.5 w-3.5" />
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function CatalogModal({
  isOpen,
  title,
  description,
  onClose,
  onSubmit,
  children,
  onReset,
  error,
  submitLabel,
  isLoading = false
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-border bg-card shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icon name="X" className="h-4 w-4" />
              </button>
            </div>

            <form className="space-y-4 px-6 py-5 max-h-[70vh] overflow-y-auto" onSubmit={onSubmit}>
              {children}

              {error ? (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
                <Button type="button" variant="outline" onClick={onReset} disabled={isLoading}>
                  Reset Form
                </Button>
                <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  <Icon name={isLoading ? "Loader2" : "Save"} className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  {isLoading ? "Saving..." : submitLabel}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function OhlgsCatalogSetupWorkspace({ activeModule, locationType, locationCode }) {
  const { user } = useAuth();

  // State declarations
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKpaId, setSelectedKpaId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [kpaForm, setKpaForm] = useState(DEFAULT_KPA_FORM);
  const [kpaError, setKpaError] = useState("");
  const [editingKpaId, setEditingKpaId] = useState(null);
  const [isKpaModalOpen, setIsKpaModalOpen] = useState(false);
  const [openKpaActionId, setOpenKpaActionId] = useState(null);
  const [kpiSetupsList, setKpiSetupsList] = useState([]);
  const [editingKpiSetup, setEditingKpiSetup] = useState(null);
  const [isKpiSetupModalOpen, setIsKpiSetupModalOpen] = useState(false);
  const [kpiSetupForm, setKpiSetupForm] = useState(DEFAULT_KPI_SETUP_FORM);
  const [kpiSetupError, setKpiSetupError] = useState("");
  const [savingKpiId, setSavingKpiId] = useState(null);
  const [assessments, setAssessments] = useState({});
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [assessmentForm, setAssessmentForm] = useState(DEFAULT_ASSESSMENT_FORM);
  const [assessmentError, setAssessmentError] = useState("");

  // React Query hooks for KPA
  const { data: kpas, isLoading: isLoadingKPAs } = useGetAllKPAs();
  const createKPA = useCreateKPA();
  const updateKPA = useUpdateKPA();

  // React Query hooks for KPI Setup
  const {
    data: kpiSetups,
    refetch: refetchKpiSetups,
    isLoading: isLoadingKpiSetups
  } = useGetKPISetupByYearAndKPA({
    year: selectedYear,
    kpaId: selectedKpaId,
  });

  const createKPISetup = useCreateKPISetup();
  const updateKPISetup = useUpdateKPISetup();

  // React Query hooks for Assessments
  const {
    refetch: refetchAssessment
  } = useGetContractPerformanceAssessmentByKpiIdAndLocation({
    kpiId: null,
    locationType,
    locationCode
  });
  const createAssessment = useCreateContractPerformanceAssessment();
  const updateAssessment = useUpdateContractPerformanceAssessment();

  // Years for selection
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i.toString());
    }
    return years;
  }, []);

  // Effect: Set default selected KPA when KPAs load
  useEffect(() => {
    if (kpas?.data && kpas.data.length > 0 && !selectedKpaId) {
      setSelectedKpaId(kpas.data[0].kpaId);
    }
  }, [kpas, selectedKpaId]);

  // Effect: Refetch KPIs when year or selected KPA changes
  useEffect(() => {
    if (selectedYear && selectedKpaId) {
      refetchKpiSetups();
    }
  }, [selectedYear, selectedKpaId, refetchKpiSetups]);

  // Effect: Process KPI Setups data
  useEffect(() => {
    if (kpiSetups?.data) {
      setKpiSetupsList(kpiSetups.data);
    } else {
      setKpiSetupsList([]);
    }
  }, [kpiSetups]);

  // Effect: Fetch assessments for each KPI Setup
  useEffect(() => {
    if (kpiSetupsList.length > 0 && locationType && locationCode) {
      const fetchAssessments = async () => {
        const assessmentsMap = {};
        for (const kpiSetup of kpiSetupsList) {
          try {
            const response = await refetchAssessment({
              kpiId: kpiSetup.kpiId,
              locationType,
              locationCode
            });
            if (response?.data?.data) {
              assessmentsMap[kpiSetup.kpiId] = response.data.data;
            }
          } catch (error) {
            console.error(`Error fetching assessment for KPI ${kpiSetup.kpiId}:`, error);
          }
        }
        setAssessments(assessmentsMap);
      };
      fetchAssessments();
    }
  }, [kpiSetupsList, locationType, locationCode, refetchAssessment]);

  // Filtered KPAs
  const filteredKpas = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return kpas?.data || [];
    return (kpas?.data || []).filter((kpa) =>
      [kpa.code, kpa.kpaName, kpa.kpaDescription, kpa.kpaType, formatStatusLabel(kpa.status)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [kpas, searchTerm]);

  // Selected KPA
  const selectedKpa = useMemo(
    () => (kpas?.data || []).find((kpa) => kpa.kpaId === selectedKpaId) || null,
    [kpas, selectedKpaId],
  );

  // Active KPA count
  const activeKpaCount = useMemo(
    () => (kpas?.data || []).filter((kpa) => isActiveStatus(kpa.status)).length,
    [kpas],
  );

  // Reset functions
  const resetKpaForm = () => {
    setKpaForm(DEFAULT_KPA_FORM);
    setKpaError("");
  };

  const resetKpiSetupForm = () => {
    setKpiSetupForm(DEFAULT_KPI_SETUP_FORM);
    setKpiSetupError("");
  };

  const resetAssessmentForm = () => {
    setAssessmentForm(DEFAULT_ASSESSMENT_FORM);
    setAssessmentError("");
  };

  // Close modal functions
  const closeKpaModal = () => {
    resetKpaForm();
    setEditingKpaId(null);
    setIsKpaModalOpen(false);
    setOpenKpaActionId(null);
  };

  const closeKpiSetupModal = () => {
    resetKpiSetupForm();
    setEditingKpiSetup(null);
    setIsKpiSetupModalOpen(false);
  };

  const closeAssessmentModal = () => {
    resetAssessmentForm();
    setEditingAssessment(null);
    setIsAssessmentModalOpen(false);
  };

  // Open modal functions
  const openAddKpaModal = () => {
    resetKpaForm();
    setEditingKpaId(null);
    setIsKpaModalOpen(true);
  };

  const openEditKpaModal = (kpa) => {
    if (!kpa) return;
    setOpenKpaActionId(null);
    setKpaForm({
      kpaName: kpa.kpaName || "",
      kpaDescription: kpa.kpaDescription || "",
      kpaType: kpa.kpaType || "BOTH",
      status: normalizeStatus(kpa.status),
    });
    setKpaError("");
    setEditingKpaId(kpa.kpaId);
    setIsKpaModalOpen(true);
  };

  const openAddKpiSetupModal = () => {
    resetKpiSetupForm();
    setEditingKpiSetup(null);
    setIsKpiSetupModalOpen(true);
  };

  const openEditKpiSetupModal = (kpiSetup) => {
    setKpiSetupForm({
      kpiName: kpiSetup.kpiName || "",
      scoringCriteria: kpiSetup.scoringCriteria || "",
      minScore: kpiSetup.minScore || 0,
      maxScore: kpiSetup.maxScore || 100,
      weight: kpiSetup.weight || 1.0,
      expectedStartDate: kpiSetup.expectedStartDate || "",
      expectedEndDate: kpiSetup.expectedEndDate || "",
      status: kpiSetup.status || "ACTIVE",
    });
    setEditingKpiSetup(kpiSetup);
    setIsKpiSetupModalOpen(true);
  };

  const openEditAssessmentModal = (kpiId, assessment) => {
    setAssessmentForm({
      observation: assessment?.observation || "",
      actualScore: assessment?.actualScore || 0,
      status: assessment?.status || "ACTIVE",
    });
    setEditingAssessment({ kpiId, assessmentId: assessment?.contractPerformanceAssessmentId });
    setIsAssessmentModalOpen(true);
  };

  // Submit handlers
  const handleKpaSubmit = async (event) => {
    event.preventDefault();
    if (!kpaForm.kpaName.trim()) {
      setKpaError("KPA name is required.");
      return;
    }

    const kpaData = {
      kpaName: kpaForm.kpaName.trim(),
      kpaDescription: kpaForm.kpaDescription.trim(),
      kpaType: kpaForm.kpaType,
      weight: kpaForm.weight,
      status: kpaForm.status,
      createdBy: user?.user?.username || "system",
    };

    try {
      if (editingKpaId) {
        await updateKPA.mutateAsync({ id: editingKpaId, payload: kpaData });
      } else {
        await createKPA.mutateAsync(kpaData);
      }
      closeKpaModal();
    } catch (error) {
      setKpaError(error?.response?.data?.message || "Error saving KPA");
    }
  };

  const handleKpiSetupSubmit = async (event) => {
    event.preventDefault();
    if (!kpiSetupForm.kpiName.trim()) {
      setKpiSetupError("KPI name is required.");
      return;
    }

    const kpiSetupData = {
      kpaId: selectedKpaId,
      year: selectedYear,
      kpiName: kpiSetupForm.kpiName.trim(),
      scoringCriteria: kpiSetupForm.scoringCriteria,
      minScore: Number(kpiSetupForm.minScore),
      maxScore: Number(kpiSetupForm.maxScore),
      weight: Number(kpiSetupForm.weight),
      expectedStartDate: kpiSetupForm.expectedStartDate || new Date(),
      expectedEndDate: kpiSetupForm.expectedEndDate || new Date(),
      status: kpiSetupForm.status,
      createdBy: user?.user?.username || "system",
    };

    try {
      setSavingKpiId(editingKpiSetup?.kpiId || 'new');
      if (editingKpiSetup) {
        await updateKPISetup.mutateAsync({ id: editingKpiSetup.kpiId, payload: kpiSetupData });
      } else {
        await createKPISetup.mutateAsync(kpiSetupData);
      }
      closeKpiSetupModal();
    } catch (error) {
      setKpiSetupError(error?.response?.data?.message || "Error saving KPI setup");
    } finally {
      setSavingKpiId(null);
    }
  };

  const handleAssessmentSubmit = async (event) => {
    event.preventDefault();

    const assessmentData = {
      kpiId: editingAssessment.kpiId,
      locationType: locationType,
      locationCode: locationCode,
      observation: assessmentForm.observation,
      actualScore: Number(assessmentForm.actualScore),
      status: assessmentForm.status,
      createdBy: user?.user?.username || "system",
    };

    try {
      if (editingAssessment.assessmentId) {
        await updateAssessment.mutateAsync({ id: editingAssessment.assessmentId, payload: assessmentData });
      } else {
        await createAssessment.mutateAsync(assessmentData);
      }

      // Refresh assessment for this KPI
      const response = await refetchAssessment({
        kpiId: editingAssessment.kpiId,
        locationType,
        locationCode
      });
      if (response?.data?.data) {
        setAssessments(prev => ({
          ...prev,
          [editingAssessment.kpiId]: response.data.data
        }));
      }

      closeAssessmentModal();
    } catch (error) {
      setAssessmentError(error?.response?.data?.message || "Error saving assessment");
    }
  };

  const handleDeleteKpiSetup = async (kpiId) => {
    if (window.confirm("Are you sure you want to delete this KPI? This will also delete all associated assessments.")) {
      alert(`Delete KPI with ID: ${kpiId}`);
    }
  };

  const handleRemoveKpa = async (kpaId) => {
    setOpenKpaActionId(null);
    alert(`Delete KPA with ID: ${kpaId}`);
  };

  if (isLoadingKPAs) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading KPA catalog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* KPA Modal */}
      <CatalogModal
        isOpen={isKpaModalOpen}
        title={editingKpaId ? "Edit OHLGS KPA" : "Add OHLGS KPA"}
        description="Maintain the parent KPA groups that define the shared OHLGS structure."
        onClose={closeKpaModal}
        onSubmit={handleKpaSubmit}
        onReset={resetKpaForm}
        error={kpaError}
        submitLabel={editingKpaId ? "Update KPA" : "Save KPA"}
        isLoading={createKPA.isLoading || updateKPA.isLoading}
      >
        <Input
          label="KPA Name"
          placeholder="Infrastructure Project Delivery"
          value={kpaForm.kpaName}
          onChange={(event) => setKpaForm((current) => ({ ...current, kpaName: event.target.value }))}
        />
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={STATUS_OPTIONS.find((option) => option.value === kpaForm.status) || null}
          onChange={(option) => setKpaForm((current) => ({ ...current, status: option?.value || "ACTIVE" }))}
        />
        <Input
          label="Weight"
          type="number"
          step="0.1"
          placeholder="1.0"
          value={kpaForm.weight}
          required
          onChange={(event) => setKpaForm((current) => ({ ...current, weight: parseFloat(event.target.value) || 0.0 }))}
        />
        <Select
          label="KPA Type"
          options={KPA_TYPE_OPTIONS}
          value={KPA_TYPE_OPTIONS.find((option) => option.value === kpaForm.kpaType) || KPA_TYPE_OPTIONS[2]}
          onChange={(option) => setKpaForm((current) => ({ ...current, kpaType: option?.value || "BOTH" }))}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">KPA Description</label>
          <textarea
            rows={4}
            value={kpaForm.kpaDescription}
            onChange={(event) => setKpaForm((current) => ({ ...current, kpaDescription: event.target.value }))}
            placeholder="Evaluation of project completion rates, quality standards, and timely delivery"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
          />
        </div>
      </CatalogModal>

      {/* KPI Setup Modal */}
      <CatalogModal
        isOpen={isKpiSetupModalOpen}
        title={editingKpiSetup ? "Edit KPI Template" : "Add New KPI Template"}
        description={`Configure KPI template for ${selectedKpa?.kpaName || "selected KPA"} - Year ${selectedYear}`}
        onClose={closeKpiSetupModal}
        onSubmit={handleKpiSetupSubmit}
        onReset={resetKpiSetupForm}
        error={kpiSetupError}
        submitLabel={editingKpiSetup ? "Update KPI" : "Add KPI"}
        isLoading={createKPISetup.isLoading || updateKPISetup.isLoading}
      >
        <Input
          label="KPI Name"
          placeholder="e.g., Number of regional coordination meetings held"
          value={kpiSetupForm.kpiName}
          required
          onChange={(event) => {
            setKpiSetupError("");
            setKpiSetupForm((current) => ({ ...current, kpiName: event.target.value }));
          }}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Scoring Criteria</label>
          <textarea
            rows={3}
            value={kpiSetupForm.scoringCriteria}
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, scoringCriteria: event.target.value }))}
            placeholder="Target: 4 meetings per year. Score 25% per meeting"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Min Score"
            type="number"
            placeholder="0"
            value={kpiSetupForm.minScore}
            required
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, minScore: parseFloat(event.target.value) || 0 }))}
          />
          <Input
            label="Max Score"
            type="number"
            placeholder="100"
            value={kpiSetupForm.maxScore}
            required
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, maxScore: parseFloat(event.target.value) || 0 }))}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Weight"
            type="number"
            step="0.1"
            placeholder="1.0"
            value={kpiSetupForm.weight}
            required
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, weight: parseFloat(event.target.value) || 0.0 }))}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((option) => option.value === kpiSetupForm.status) || null}
            onChange={(option) => setKpiSetupForm((current) => ({ ...current, status: option?.value || "ACTIVE" }))}
          />
        </div>
        {/* <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Expected Start Date"
            type="date"
            value={kpiSetupForm.expectedStartDate}
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, expectedStartDate: event.target.value }))}
          />
          <Input
            label="Expected End Date"
            type="date"
            value={kpiSetupForm.expectedEndDate}
            onChange={(event) => setKpiSetupForm((current) => ({ ...current, expectedEndDate: event.target.value }))}
          />
        </div> */}
      </CatalogModal>

      {/* Assessment Modal */}
      <CatalogModal
        isOpen={isAssessmentModalOpen}
        title={editingAssessment?.assessmentId ? "Edit Performance Assessment" : "Add Performance Assessment"}
        description={`Assess performance for this KPI at ${locationType} - ${locationCode}`}
        onClose={closeAssessmentModal}
        onSubmit={handleAssessmentSubmit}
        onReset={resetAssessmentForm}
        error={assessmentError}
        submitLabel={editingAssessment?.assessmentId ? "Update Assessment" : "Save Assessment"}
        isLoading={createAssessment.isLoading || updateAssessment.isLoading}
      >
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Observation / Notes</label>
          <textarea
            rows={3}
            value={assessmentForm.observation}
            onChange={(event) => setAssessmentForm((current) => ({ ...current, observation: event.target.value }))}
            placeholder="Add observations about performance"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <Input
          label="Actual Score"
          type="number"
          step="0.01"
          placeholder="0"
          value={assessmentForm.actualScore}
          required
          onChange={(event) => setAssessmentForm((current) => ({ ...current, actualScore: parseFloat(event.target.value) || 0 }))}
        />
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={STATUS_OPTIONS.find((option) => option.value === assessmentForm.status) || null}
          onChange={(option) => setAssessmentForm((current) => ({ ...current, status: option?.value || "ACTIVE" }))}
        />
      </CatalogModal>

      {/* Header Section */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Icon name={activeModule.icon} className="h-3.5 w-3.5" />
              OHLGS Setup Workspace
            </div>
            <h2 className="text-2xl font-semibold text-foreground">{activeModule.label}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{activeModule.summary}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              Configure KPI templates first, then add performance assessments for each location.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Total KPAs</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{kpas?.data?.length || 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Active KPAs</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{activeKpaCount}</p>
              </div>
            </div>

            <Button type="button" onClick={openAddKpaModal}>
              <Icon name="Plus" className="h-4 w-4" />
              Add KPA
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        {/* KPA Table */}
        <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <Icon name="ClipboardList" className="h-3.5 w-3.5" />
              Shared OHLGS KPA Library
            </div>
            <h3 className="mt-3 text-lg font-semibold text-foreground">Shared OHLGS KPA Catalog</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Select a KPA to configure its KPIs for a specific year.
            </p>
          </div>

          <div className="mb-4 relative">
            <label className="sr-only" htmlFor="ohlgs-kpa-search">Search KPAs</label>
            <Icon
              name="Search"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="ohlgs-kpa-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by KPA code, name, type, or status"
              className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">KPA Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">KPA Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {filteredKpas.map((kpa, index) => {
                  const isSelected = selectedKpa?.kpaId === kpa.kpaId;
                  return (
                    <tr
                      key={kpa.kpaId}
                      className={`cursor-pointer transition-colors ${isSelected ? "bg-muted" : "hover:bg-muted/30"
                        }`}
                      onClick={() => setSelectedKpaId(kpa.kpaId)}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {getKpaDisplayCode(kpa, index)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{kpa.kpaName}</p>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {kpa.kpaDescription}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground">{kpa?.weight?.toFixed(1) || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${kpa.kpaType === "REGIONAL" ? "bg-blue-100 text-blue-800" :
                          kpa.kpaType === "DISTRICT" ? "bg-green-100 text-green-800" :
                            "bg-purple-100 text-purple-800"
                          }`}>
                          {kpa.kpaType}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${isActiveStatus(kpa.status)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {formatStatusLabel(kpa.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <RowActionMenu
                            isOpen={openKpaActionId === kpa.kpaId}
                            onToggle={() =>
                              setOpenKpaActionId((current) => (current === kpa.kpaId ? null : kpa.kpaId))
                            }
                            items={[
                              {
                                key: "edit",
                                label: "Edit",
                                icon: "Edit",
                                onClick: () => openEditKpaModal(kpa),
                              },
                              {
                                key: "remove",
                                label: "Remove",
                                icon: "Trash2",
                                tone: "destructive",
                                onClick: () => handleRemoveKpa(kpa.kpaId),
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredKpas.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {searchTerm ? "No KPAs match the current search." : "No KPAs added yet. Click 'Add KPA' to create one."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>

        {/* KPI Configuration & Assessment Panel */}
        {selectedKpa ? (
          <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    <Icon name="Target" className="h-3.5 w-3.5" />
                    KPI Management
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">{selectedKpa.kpaName}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Create KPI templates
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-foreground">Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground"
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <Button type="button" size="sm" onClick={openAddKpiSetupModal}>
                    <Icon name="Plus" className="h-3.5 w-3.5" />
                    Add KPI Template
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-card">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">KPI Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Score Range</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Weight</th>
                      {/* <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Scoring Criteria</th> */}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoadingKpiSetups ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          <div className="flex items-center justify-center gap-2">
                            <Icon name="Loader2" className="h-4 w-4 animate-spin" />
                            Loading KPIs...
                          </div>
                        </td>
                      </tr>
                    ) : kpiSetupsList.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          No KPI templates configured for {selectedYear}. Click "Add KPI Template" to create one.
                        </td>
                      </tr>
                    ) : (
                      kpiSetupsList.map((kpiSetup) => {
                        const assessment = assessments[kpiSetup.kpiId];
                        const hasAssessment = assessment && assessment.contractPerformanceAssessmentId;

                        return (
                          <tr key={kpiSetup.kpiId}>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-foreground">{kpiSetup.kpiName}</p>
                                {kpiSetup.scoringCriteria && (
                                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{kpiSetup.scoringCriteria}</p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {kpiSetup.minScore} - {kpiSetup.maxScore}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{kpiSetup.weight}</td>

                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${kpiSetup.status === "ACTIVE"
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}>
                                {kpiSetup.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => openEditKpiSetupModal(kpiSetup)}
                                  className="p-1 text-muted-foreground hover:text-foreground"
                                  title="Edit KPI Template"
                                >
                                  <Icon name="Edit" className="h-4 w-4" />
                                </button>


                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Info Note */}
              <div className="rounded-lg bg-blue-50 p-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Icon name="Info" className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-blue-800">
                    <strong>Two-step process:</strong> First create KPI templates (name, scoring criteria, weight).
                    Then click the <Icon name="ClipboardList" className="h-3 w-3 inline" /> icon to add/update performance assessments for {locationType} - {locationCode}.
                  </span>
                </div>
              </div>

              {/* KPA Type Info */}
              <div className="rounded-lg bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Icon name="Target" className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    KPA Type: <strong>{selectedKpa.kpaType}</strong> -
                    {selectedKpa.kpaType === "REGIONAL" && " This KPA applies to Regional level only"}
                    {selectedKpa.kpaType === "DISTRICT" && " This KPA applies to District level only"}
                    {selectedKpa.kpaType === "BOTH" && " This KPA applies to both Regional and District levels"}
                  </span>
                </div>
              </div>
            </div>
          </article>
        ) : (
          <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Icon name="Target" className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">No KPA Selected</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-md">
                Please select a KPA from the left panel to configure its KPIs for different years.
              </p>
            </div>
          </article>
        )}
      </section>
    </div>
  );
}